/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import dayjs from 'dayjs';
import { pick } from 'es-toolkit';
import { graphql, HttpResponse } from 'msw';

import {
  UserDefaultTeamIdDocument,
  ValidateTeamIdDocument,
  type UserDefaultTeamIdQuery,
  type ValidateTeamIdQuery,
  type TeamRoleQuery,
  TeamRoleDocument,
  ClientTeamMembershipRole,
  type TeamServerlessEndpointsPlanQuery,
  TeamServerlessEndpointsPlanDocument,
  ServerlessSubplanTier,
  type TeamContainerPlanQuery,
  TeamContainerPlanDocument,
  ContainerSubplanTier,
  ContainerSubplanState,
  type UserTeamsQuery,
  UserTeamsDocument,
  type TeamNameQuery,
  TeamNameDocument,
  type TeamPaymentQuery,
  TeamPaymentDocument,
  ServerlessSubplanState,
  type TeamDedicatedEndpointsPlanQuery,
  TeamDedicatedEndpointsPlanDocument,
  type DedicatedTeamMembersQuery,
  DedicatedTeamMembersDocument,
  ClientUserSuperRoleEnum,
  IntegrationsContextDocument,
  type IntegrationsContextQuery,
  DedicatedSubplanTier,
  DedicatedEndpointEnableLoggingDocument,
  TeamPaymentCardsDocument,
  type TeamPaymentCardsQuery,
  type TeamCheckStripeSetupIntentCompletionMutation,
  TeamCheckStripeSetupIntentCompletionDocument,
  DedicatedModelDetailHeaderDocument,
} from '@fai/friendli-suite/generated/client/graphql';
import {
  aClientTeam,
  aClientTeamBilling,
  aClientTeamBillingStripe,
  aClientTeamBillingStripeIntent,
  aClientTeamStripePayment,
  aClientTeamStripePaymentMethod,
  aClientUser,
  aClientUserCreateAvatarUploadUrlSuccess,
  aClientUserIntegrationHuggingFace,
  aClientUserIntegrationHuggingfaceDisconnectedError,
  aClientUserIntegrationHuggingfaceNotFoundError,
  aClientUserIntegrationWandb,
  aClientUserIntegrationWandbDisconnectedError,
  aClientUserIntegrationWandbNotFoundError,
} from '@fai/friendli-suite/generated/mockdata';

export const mockUser = {
  id: 'sq3mEBoLtCvw',
  name: aClientUser().name,
  avatarUrl: 'https://picsum.photos/100',
  avatarUpload: {
    __typename: 'ClientUserCreateAvatarUploadUrlSuccess' as const,
    uploadInfo: aClientUserCreateAvatarUploadUrlSuccess().uploadInfo,
  },
  email: 'test@test.com',
  superRoles: {
    roles: [
      ClientUserSuperRoleEnum.SuperAdmin,
      ClientUserSuperRoleEnum.SuperReadonlyAdmin,
      ClientUserSuperRoleEnum.SuperContainerAdmin,
    ],
  },
  integrations: {
    huggingface: {
      ...aClientUserIntegrationHuggingFace(),
      ...aClientUserIntegrationHuggingfaceNotFoundError(),
      ...aClientUserIntegrationHuggingfaceDisconnectedError(),
    },
    wandb: {
      ...aClientUserIntegrationWandb(),
      ...aClientUserIntegrationWandbNotFoundError(),
      ...aClientUserIntegrationWandbDisconnectedError(),
    },
  },
  teams: [
    {
      id: 'k5whsqghlB6k',
      name: aClientTeam().name,
    },
  ],
  defaultTeam: {
    id: 'k5whsqghlB6k',
    name: 'test-team',
    segment: 'fai-demo',
    role: ClientTeamMembershipRole.Owner,
    payment: {
      __typename: 'ClientTeamStripePayment' as
        | 'ClientTeamStripePayment'
        | 'ClientTeamAwsMarketplacePayment'
        | undefined,
      state: aClientTeamStripePayment().state,
      stripeCustomerId: aClientTeamStripePayment().stripeCustomerId,
      stripeCustomerPortalUrl: aClientTeamStripePayment().stripeCustomerPortalUrl,
    },
    billing: {
      monthlyPayment: aClientTeamBilling().monthlyPayment,
      nextPaymentDate: dayjs(aClientTeamBilling().nextPaymentDate),
      stripe: {
        customerId: aClientTeamBillingStripe().customerId,
        paymentMethods: [
          {
            brand: aClientTeamStripePaymentMethod().card?.brand,
            expMonth: aClientTeamStripePaymentMethod().card?.expMonth,
            expYear: aClientTeamStripePaymentMethod().card?.expYear,
            last4: aClientTeamStripePaymentMethod().card?.last4,
            isDefault: aClientTeamStripePaymentMethod().isDefault,
            paymentMethodId: aClientTeamStripePaymentMethod().id,
            updatedAt: dayjs(aClientTeamStripePaymentMethod().updatedAt),
          },
        ],
      },
    },
    stripeIntent: {
      clientSecret: aClientTeamBillingStripeIntent().clientSecret,
    },
    productAccess: {
      serverlessEndpoints: true,
      dedicatedEndpoints: true,
      container: true,
    },
    productServerless: {
      teamId: 'k5whsqghlB6k',
      tier: ServerlessSubplanTier.Enterprise,
      state: ServerlessSubplanState.Approved,
      freeCreditUsage: {
        total: '5',
        used: '0.0019',
      },
    },
    productDedicated: {
      id: 'k5whsqghlB6k',
      tier: DedicatedSubplanTier.Enterprise,
      freeCreditUsage: {
        total: '5',
        used: '0.0019',
      },
      team: {
        members: [
          {
            id: 'sq3mEBoLtCvw',
            name: 'test-user',
            email: 'test-member@test.com',
            avatarUrl: 'https://picsum.photos/100',
          },
        ],
      },
      projects: [
        {
          id: 'bsu1fXSBrPxY',
          name: 'test-project',
        },
      ],
      endpoint: {
        id: 'qndw5uk2q6i2',
        createdAt: dayjs(new Date(2024, 1, 1)),
        updatedAt: dayjs(new Date(2025, 1, 1)),
        advancedConfig: {
          enableContentLogging: true,
        },
      },
      model: [
        {
          id: 'id',
          name: 'test-model',
          createdAt: dayjs(new Date(2024, 1, 1)),
          createdBy: { id: 'user-1', name: 'user-1' },
          updatedAt: dayjs(new Date(2025, 1, 1)),
        },
      ],
    },
    productContainer: {
      teamId: 'k5whsqghlB6k',
      tier: ContainerSubplanTier.Enterprise,
      state: ContainerSubplanState.Approved,
      expiresAt: dayjs(new Date(2025, 12, 25)),
    },
  },
};

const useIntegrationsContextQuery = graphql.query<IntegrationsContextQuery>(
  IntegrationsContextDocument,
  () =>
    HttpResponse.json({
      data: {
        clientSession: {
          user: {
            id: mockUser.id,
            name: mockUser.name,
            email: mockUser.email,
            superRoles: mockUser.superRoles,
          },
        },
        clientTeam: {
          id: mockUser.defaultTeam.id,
          name: mockUser.defaultTeam.name,
          segment: mockUser.defaultTeam.segment,
          serverlessSubplan: pick(mockUser.defaultTeam.productServerless, ['teamId', 'tier']),
          dedicatedSubplan: pick(mockUser.defaultTeam.productDedicated, ['id', 'tier']),
          containerSubplan: pick(mockUser.defaultTeam.productContainer, ['teamId', 'tier']),
        },
      },
    })
);

const useTeamPaymentQuery = graphql.query<TeamPaymentQuery>(TeamPaymentDocument, () =>
  HttpResponse.json({
    data: {
      clientSession: {
        user: {
          id: mockUser.id,
          team: {
            id: mockUser.defaultTeam.id,
            team: {
              id: mockUser.defaultTeam.id,
              payment: {
                state: mockUser.defaultTeam.payment.state,
              },
            },
          },
        },
      },
    },
  })
);

const useTeamNameQuery = graphql.query<TeamNameQuery>(TeamNameDocument, () =>
  HttpResponse.json({
    data: {
      clientTeam: {
        id: mockUser.teams[0].id,
        name: mockUser.teams[0].name,
      },
    },
  })
);

const useValidateTeamIdQuery = graphql.query<ValidateTeamIdQuery>(ValidateTeamIdDocument, () =>
  HttpResponse.json({
    data: {
      clientTeam: {
        id: mockUser.teams[0].id,
        name: mockUser.teams[0].name,
      },
    },
  })
);

const useUserDefaultTeamIdQuery = graphql.query<UserDefaultTeamIdQuery>(
  UserDefaultTeamIdDocument,
  () =>
    HttpResponse.json({
      data: {
        clientSession: {
          user: {
            id: mockUser.id,
            teamDefault: {
              team: {
                id: mockUser.teams[0].id,
              },
            },
          },
        },
      },
    })
);

const usePaymentStateQuery = graphql.query<TeamPaymentQuery>(TeamPaymentDocument, () =>
  HttpResponse.json({
    data: {
      clientSession: {
        user: {
          id: mockUser.id,
          team: {
            id: mockUser.defaultTeam.id,
            team: {
              id: mockUser.defaultTeam.id,
              payment: {
                state: mockUser.defaultTeam.payment.state,
              },
            },
          },
        },
      },
    },
  })
);

const useTeamRoleQuery = graphql.query<TeamRoleQuery>(TeamRoleDocument, () =>
  HttpResponse.json({
    data: {
      clientSession: {
        user: {
          id: mockUser.id,
          team: {
            id: mockUser.defaultTeam.id,
            role: mockUser.defaultTeam.role,
          },
        },
      },
    },
  })
);

const useTeamServerlessEndpointsPlanQuery = graphql.query<TeamServerlessEndpointsPlanQuery>(
  TeamServerlessEndpointsPlanDocument,
  () =>
    HttpResponse.json({
      data: {
        clientSession: {
          user: {
            id: mockUser.id,
            team: {
              id: mockUser.defaultTeam.id,
              role: mockUser.defaultTeam.role,
              productAccess: {
                serverlessEndpoints: mockUser.defaultTeam.productAccess.serverlessEndpoints,
              },
              team: {
                id: mockUser.defaultTeam.id,
                serverlessSubplan: {
                  ...mockUser.defaultTeam.productServerless,
                },
              },
            },
          },
        },
      },
    })
);

const useTeamDedicatedEndpointsPlanQuery = graphql.query<TeamDedicatedEndpointsPlanQuery>(
  TeamDedicatedEndpointsPlanDocument,
  () =>
    HttpResponse.json({
      data: {
        clientSession: {
          user: {
            id: mockUser.id,
            team: {
              id: mockUser.defaultTeam.id,
              role: mockUser.defaultTeam.role,
              productAccess: {
                dedicatedEndpoints: mockUser.defaultTeam.productAccess.dedicatedEndpoints,
              },
              team: {
                id: mockUser.defaultTeam.id,
                dedicatedSubplan: {
                  ...mockUser.defaultTeam.productDedicated,
                },
              },
            },
          },
        },
      },
    })
);

const useTeamContainerPlanQuery = graphql.query<TeamContainerPlanQuery>(
  TeamContainerPlanDocument,
  () =>
    HttpResponse.json({
      data: {
        clientSession: {
          user: {
            id: mockUser.id,
            team: {
              id: mockUser.defaultTeam.id,
              role: mockUser.defaultTeam.role,
              productAccess: {
                container: mockUser.defaultTeam.productAccess.container,
              },
              team: {
                id: mockUser.defaultTeam.id,
                containerSubplan: {
                  ...mockUser.defaultTeam.productContainer,
                },
              },
            },
          },
        },
      },
    })
);

const useDedicatedTeamMembersQuery = graphql.query<DedicatedTeamMembersQuery>(
  DedicatedTeamMembersDocument,
  () =>
    HttpResponse.json({
      data: {
        dedicatedSubplan: {
          id: mockUser.defaultTeam.id,
          team: {
            members: {
              edges: mockUser.defaultTeam.productDedicated.team.members.map((member) => ({
                id: '',
                node: member,
                productAccess: {
                  dedicatedEndpoints: mockUser.defaultTeam.productAccess.dedicatedEndpoints,
                },
              })),
            },
          },
        },
      },
    })
);

const useUserTeamsQuery = graphql.query<UserTeamsQuery>(UserTeamsDocument, () =>
  HttpResponse.json({
    data: {
      clientSession: {
        user: {
          id: mockUser.id,
          teams: {
            edges: mockUser.teams.map((team) => ({ node: team })),
          },
        },
      },
    },
  })
);

const useCheckStripeSetupIntentCompletionMutation =
  graphql.mutation<TeamCheckStripeSetupIntentCompletionMutation>(
    TeamCheckStripeSetupIntentCompletionDocument,
    () =>
      HttpResponse.json({
        data: {
          clientTeamCheckStripeSetupIntentCompletion: {
            __typename: 'ClientTeamBillingStripeSetupIntentState',
          },
        },
      })
  );

const usePaymentCardsQuery = graphql.query<TeamPaymentCardsQuery>(TeamPaymentCardsDocument, () =>
  HttpResponse.json({
    data: {
      clientTeam: {
        id: mockUser.defaultTeam.id,
        billing: {
          stripe: {
            customerId: mockUser.defaultTeam.billing.stripe.customerId,
            paymentMethods: {
              edges: mockUser.defaultTeam.billing.stripe.paymentMethods.map((pm) => ({
                node: {
                  card: {
                    brand: pm.brand,
                    last4: pm.last4,
                    expYear: pm.expYear,
                    expMonth: pm.expMonth,
                  },
                  isDefault: pm.isDefault,
                  id: pm.paymentMethodId,
                  updatedAt: pm.updatedAt,
                },
              })),
            },
          },
        },
      },
    },
  })
);

// const useDedicatedTeamMembersQuery = graphql.query<DedicatedTeamMembersQuery>(
//   DedicatedTeamMembersDocument,
//   () =>
//     HttpResponse.json({
//       data: {
//         dedicatedSubplan: {
//           id: mockUser.defaultTeam.id,
//           team: {
//             members: {
//               edges: mockUser.defaultTeam.productDedicated.team.members.map(
//                 (member) => ({
//                   id: '',
//                   node: member,
//                   productAccess: {
//                     dedicatedEndpoints:
//                       mockUser.defaultTeam.productAccess.dedicatedEndpoints,
//                   },
//                 })
//               ),
//             },
//           },
//         },
//       },
//     })
// );

const useEndpointEnableLogging = graphql.query(DedicatedEndpointEnableLoggingDocument, () =>
  HttpResponse.json({
    data: {
      dedicatedEndpoint: {
        id: mockUser.id,
        advancedConfig: {
          enableContentLogging:
            mockUser.defaultTeam.productDedicated.endpoint.advancedConfig.enableContentLogging,
        },
      },
    },
  })
);

const useDedicatedModelDetailHeaderQuery = graphql.query(DedicatedModelDetailHeaderDocument, () =>
  HttpResponse.json({
    data: {
      dedicatedModel: {
        id: mockUser.defaultTeam.productDedicated.model[0].id,
        name: mockUser.defaultTeam.productDedicated.model[0].name,
      },
    },
  })
);

const validationHandlers = [
  useIntegrationsContextQuery,
  useUserDefaultTeamIdQuery,
  useValidateTeamIdQuery,
  useTeamRoleQuery,
  useTeamDedicatedEndpointsPlanQuery,
  useTeamServerlessEndpointsPlanQuery,
  useTeamContainerPlanQuery,
  useUserTeamsQuery,
  useTeamNameQuery,
  useTeamPaymentQuery,
  useEndpointEnableLogging,
  useDedicatedTeamMembersQuery,
  usePaymentCardsQuery,
  usePaymentStateQuery,
  useCheckStripeSetupIntentCompletionMutation,
  useDedicatedModelDetailHeaderQuery,
];

export default validationHandlers;
